// Edit layer style main

import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, IconButton, Stack, Typography } from "@mui/material";
import { theme_errorRed, theme_orange, theme_textColorBlended, theme_textColorMain } from "../../../Theme";
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from "react";
import { EditLayerStyleEditor, GetDefaultLayerStyle } from "./EditLayerStyleEditor";
import YesNoDialog from "../../../Components/YesNoDialog";
import { ILayer } from "../../../Layers/LayerInterfaces";
import { ILayerStyleData, ILayerStyle, TLayerStyleShapeType, ILayerStyleRule } from "./LayerStyleInterfaces";
import { ValidateLayerStyleRule } from "./EditLayerStyleRule";


export const CURRENT_LAYER_STYLE_VERSION = 2;


//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyleMainProps
{
  layer?: ILayer;
  layerStyleData?: ILayerStyleData;
  setLayerStyleData: any;
  setStyleWasChanged: any;
}

//-------------------------------------------------------------------------------
// Edit layer style main
//-------------------------------------------------------------------------------
export function EditLayerStyleMain(props: EditLayerStyleMainProps) 
{


  // Keeps track of which style accordion panel is open (only one can be open at a time)
  const [styleIdOpenForEditing, setStyleIdOpenForEditing] = useState<number|undefined>(undefined);

  const [deleteStyleConfirmation, setDeleteStyleConfirmation] = useState<boolean>(false);
  const [deleteStyle, setDeleteStyle] = useState<ILayerStyle|undefined>(undefined);






  //-------------------------------------------------------------------------------
  // One-time init.
  //-------------------------------------------------------------------------------
  useEffect(() => 
  {
    // When the UI is first opened, auto-expand the active style
    if(props.layerStyleData && props.layerStyleData.active_style_id)
      setStyleIdOpenForEditing(props.layerStyleData.active_style_id);

  }, [props.layerStyleData?.active_style_id]);

  //-------------------------------------------------------------------------------
  // A style's on/off checkbox was changed.
  // 
  // NOTE: We use onClick instead of onChange because onClick allows us to stop
  //       propagation of the mouse click event (otherwise it would trigger the accordion).  
  //-------------------------------------------------------------------------------
  const OnStyleCheckBoxClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, style: ILayerStyle) => 
  {
    if(!props.layerStyleData || !style) return;

    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    const updatedStyleData: ILayerStyleData =
    {
      ...props.layerStyleData,
      active_style_id: style.id === props.layerStyleData.active_style_id ? undefined : style.id,
    }

    props.setLayerStyleData(updatedStyleData);
    props.setStyleWasChanged(true);
  }

  //-------------------------------------------------------------------------------
  // A style's delete buttons was clicked.
  //-------------------------------------------------------------------------------
  const OnConfirmDeleteStyle = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, style: ILayerStyle) => 
  {
    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    setDeleteStyle(style);
    setDeleteStyleConfirmation(true);
  }
  
  //-------------------------------------------------------------------------------
  // 
  //-------------------------------------------------------------------------------  
  function OnDeleteStyle()
  {
    setDeleteStyleConfirmation(false);

    if(!props.layerStyleData || !deleteStyle) return;

    const updatedStyleData: ILayerStyleData =
    {
      ...props.layerStyleData,
      styles: props.layerStyleData.styles.filter(s => s.id !== deleteStyle.id)
    }

    if(props.layerStyleData.active_style_id === deleteStyle.id)
      updatedStyleData.active_style_id = undefined;

    props.setLayerStyleData(updatedStyleData);
    props.setStyleWasChanged(true);
  }

  //-------------------------------------------------------------------------------
  // A style's accordion was expanded or collapsed.
  //-------------------------------------------------------------------------------
  const OnAccordionExpandChange = (event: React.SyntheticEvent<Element, Event>, expanded: boolean, clickedStyle: ILayerStyle) => 
  {
    if(!props.layerStyleData) return;

    if(expanded === true)
      setStyleIdOpenForEditing(clickedStyle.id);
    else
      setStyleIdOpenForEditing(undefined);
  }

  //-------------------------------------------------------------------------------
  // 
  //-------------------------------------------------------------------------------  
  function OnCreateNewStyle()
  {
    if(!props.layerStyleData) return;

    const newStyle: ILayerStyle = GetDefaultLayerStyle(props.layerStyleData.styles,props.layerStyleData.layer_shape_type);

    const updatedStyleData: ILayerStyleData =
    {
      ...props.layerStyleData,
      styles: [...props.layerStyleData.styles, newStyle],
      active_style_id: newStyle.id,
    }

    props.setLayerStyleData(updatedStyleData);
    props.setStyleWasChanged(true);

    // Auto-open the accordion for this new style
    setStyleIdOpenForEditing(newStyle.id);
  }

  //-------------------------------------------------------------------------------
  // A style was updated in the editor sub-component - update the style data.
  //-------------------------------------------------------------------------------
  function OnStyleUpdated(updatedStyle: ILayerStyle)
  {
    if(!props.layerStyleData) return;

    const updatedStyleData: ILayerStyleData =
    {
      ...props.layerStyleData,
      styles: props.layerStyleData.styles.map(oldStyle => oldStyle.id === updatedStyle.id ? updatedStyle : oldStyle)
    }

    props.setLayerStyleData(updatedStyleData);
    props.setStyleWasChanged(true);
  }
  








  

  if(!props.layer) return null;

  if(props.layer.geoserverSourceType === 'raster') return (
    <Stack>
      <Typography sx={{ mt: 3, fontSize: '1.5rem', color: theme_errorRed, textAlign: 'center', opacity: 0.7 }}>
        This is a raster layer
      </Typography>
      <Typography sx={{ mt: 1, fontSize: '1.0rem', color: theme_errorRed, textAlign: 'center', opacity: 0.6 }}>
        Only vector layers can be styled with this tool
      </Typography>
    </Stack>
    )
  
  if(!props.layerStyleData) return (
    <Typography sx={{ mt: 3, fontSize: '1.5rem', color: theme_errorRed, textAlign: 'center', opacity: 0.7 }}>
      This layer's style cannot be edited.
    </Typography>
  )

  // Main render

  return (

    <Stack direction='column'>

      <Typography sx={{ mb: 2, color: theme_textColorBlended, fontSize: '0.9rem' }}>
        You can create multiple styles for this layer, however only one can be active at a time.  All users of this layer will see the style that is selected here.
      </Typography>

      {props.layerStyleData.styles.map(function(style)
      {
        if(!props.layerStyleData) return null;

        return (

        <Accordion expanded={style.id === styleIdOpenForEditing } key={style.id} disableGutters={true} 
                   slotProps={{ transition: { unmountOnExit: true } }}
                   sx={{ my: 0.5, width: '100%', boxShadow: 0, bgcolor: 'transparent', p: 0 }} 
                         onChange={(e,expanded) => OnAccordionExpandChange(e,expanded,style)}>

            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ opacity: 1, height: '1rem', p: 0 }} />}
                              sx={{ width: '100%', p: 0, bgcolor: '#1A4373', borderRadius: 2 }}>
              <Stack sx={{ mx:1, width: '100%' }}>

                <Stack direction='row' sx={{ width: '100%', mx: 1, my: 0.2, alignItems: 'center', justifyContent: 'left' }}>

                  <Checkbox size='large' checked={style.id === props.layerStyleData?.active_style_id}
                            onClick={(e)=>OnStyleCheckBoxClicked(e,style)}
                            sx=
                            {{ 
                              ml: '-14px', mr: 1, color: theme_textColorBlended, width: '40px', height: '40px',
                              '&.Mui-checked': { color: theme_textColorBlended } 
                            }} />

                  <Stack sx={{ width: '100%' }}>

                    <Stack direction='row'>
                      <Typography sx={{ color: theme_textColorBlended+'D0', fontSize: '1.1rem', fontWeight: 'bold' }}>
                        Style
                      </Typography>
                      <Typography sx={{ ml: 2, color: theme_orange+'D0', fontSize: '1.1rem' }}>
                        {style.name}
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: theme_textColorMain, opacity: 0.6, fontSize: '0.7rem' }}>
                      {style.description}
                    </Typography>
                  </Stack>

                  {/* Style utility buttons */}

                  <IconButton sx={{ p: 0.3, opacity: 0.8 }} onClick={(e)=>OnConfirmDeleteStyle(e,style)}>
                    <DeleteIcon sx={{ width: '20px', height: '20px', color: theme_textColorBlended }} />
                  </IconButton>
                </Stack>

              </Stack>

            </AccordionSummary>
            
            <AccordionDetails sx={{ bgcolor: '#1A4373', mt: '-12px', borderRadius: 2, p:'3px' }}>
              <Stack sx={{ bgcolor: '#143358', borderRadius: 1.5, p:'8px', mt: 1 }}>

                <EditLayerStyleEditor style={style} OnStyleUpdated={OnStyleUpdated} 
                                      layer_shape_type={props.layerStyleData.layer_shape_type} 
                                      layer={props.layer}/>

              </Stack>
            </AccordionDetails>

        </Accordion>
      )}
      )}
      
      <Stack sx={{ mt: 4, alignItems: 'center', width: '100%' }}>
        <Button variant='outlined' sx={{ bgcolor: theme_errorRed+'20', color: '#D0A0A0', textTransform: 'none' }} onClick={()=>OnCreateNewStyle()}>
          Create a New Style
        </Button>
      </Stack>



      {/* "Delete Style" Yes/No confirmation dialog */}

      <YesNoDialog open={deleteStyleConfirmation} setOpen={setDeleteStyleConfirmation}
                   onYes={OnDeleteStyle}>
        <Stack direction='row' sx={{ alignItems: 'center' }}>
          <DeleteIcon sx={{ mr: 2, color: theme_textColorBlended }} />
          <Box sx={{ color: theme_textColorMain }}>Delete style</Box>
          <Box sx={{ ml: 1, color: theme_orange, fontWeight: 'bold' }}>{deleteStyle?.name}</Box>
          <Box sx={{ ml: 1, color: theme_textColorMain }}>?</Box>
        </Stack>
        <Typography sx={{ fontSize: '0.7rem', color: theme_textColorBlended }}>
          {deleteStyle?.description}
        </Typography>
      </YesNoDialog>
      
    </Stack>
  )
}





//-------------------------------------------------------------------------------
//
//-------------------------------------------------------------------------------
export function GetLayerStyleShapeType(layer: ILayer): TLayerStyleShapeType
{
  switch(layer.geoserverVectorType)
  {
    case 'point': return 'point';
    case 'line': return 'line';
    case 'polygon': return 'polygon';
  }
}

//-------------------------------------------------------------------------------
// Generates default/empty layer style data.
//-------------------------------------------------------------------------------

export function GetDefaultLayerStyleData(shape_type: TLayerStyleShapeType): ILayerStyleData
{
  const newStyle: ILayerStyleData = 
  {
    version: CURRENT_LAYER_STYLE_VERSION,
    layer_shape_type: shape_type,
    active_style_id: 1,
    styles: []
  }

  return newStyle;
}

//-------------------------------------------------------------------------------
// Validate the specified layer style data.
//
// Returns an error message if validation FAILS, or empty string if it succeeds.
// The error message tells the user what style and rule the error occurred in.
//-------------------------------------------------------------------------------
export function ValidateLayerStyleData(styleData: ILayerStyleData|undefined): string
{
  if(!styleData) return 'Invalid layer style data' ;

  // Validate each style

  for(let i=0; i < styleData.styles.length; i++)
  {
    const style: ILayerStyle = styleData.styles[i];

    if(!style.id || style.id <= 0)
      return `[Style ${i+1}] Invalid style id`;

    if(!style.rules || style.rules.length === 0)
      return `[Style ${i+1}] Each style must have at least one rule`;

    // Validate the special global defaults rule

    if(style.default_rule === undefined)
      return `[Style ${i+1}] Default rule is undefined`;

    let errMsg: string = ValidateLayerStyleRule(styleData.layer_shape_type, style.default_rule, style.default_rule, `[Style ${i+1} Global Defaults] `);
    if(errMsg !== '') return errMsg;

    // Validate each rule

    for(let j=0; j < style.rules.length; j++)
    {
      errMsg = ValidateLayerStyleRule(styleData.layer_shape_type, style.rules[j], style.default_rule, `[Style ${i+1} Rule ${j+1}] `);
      if(errMsg !== '') return errMsg;
    }
  }

  // Success
  return '';
}